import { useState, useEffect } from 'react'
import api from '../api/client'
import Modal from './Modal'
import Boton from './Boton'
import { PROVINCIAS, esCapitalFederal, matchProvincia } from '../data/argentina'
import CampoLocalidad from './CampoLocalidad'
import CampoDireccion from './CampoDireccion'
import { proveedoresApi, npApi } from '../api/compras'

const TIPOS_DOC = [
  { value: 80, label: 'CUIT' },
  { value: 85, label: 'CUIL' },
  { value: 96, label: 'DNI' },
]

const TIPOS_RESP = [
  { value: 1.0, label: 'Responsable Inscripto (RI)' },
  { value: 3.1, label: 'Monotributo' },
  { value: 4.0, label: 'Exento' },
  { value: 5.0, label: 'Consumidor Final' },
]

const EMPTY = {
  nombre: '', nombre_fantasia: '', tipo_doc: '', cuit: '', tipo_resp: '',
  domicilio: '', localidad: '', provincia: '', codigo_postal: '',
  email: '', telefono: '', vendedor: '', condicion: '', rubro: '',
}

/*
 * Alta de proveedor desde LGR (Martin, 15 sep 2026). Vive solo en LGR: no tiene
 * código de Presea y el sync diario no lo toca. Mismo formulario que el alta de
 * cliente de la NV (ARCA completa razón social, condición y domicilio; la
 * dirección no se recorta), con los datos que usa Compras.
 */
export default function ModalAltaProveedor({ onClose, onCreado }) {
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [errores, setErrores] = useState({})
  const [afipStatus, setAfipStatus] = useState(null)
  const [afipMsg, setAfipMsg] = useState('')
  const [condiciones, setCondiciones] = useState([])

  useEffect(() => {
    npApi.catalogos().then(({ data }) => setCondiciones(data.condiciones_pago || [])).catch(() => setCondiciones([]))
  }, [])

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }))
  const fieldErr = (key) => errores[key]?.[0]
  const provinciaCanon = matchProvincia(form.provincia)
  const esCaba = esCapitalFederal(provinciaCanon)

  const validarAfip = async () => {
    const cuit = form.cuit.replace(/\D/g, '')
    if (cuit.length !== 11) { setAfipMsg('Ingresá un CUIT de 11 dígitos.'); setAfipStatus('error'); return }
    setAfipStatus('loading')
    setAfipMsg('')
    try {
      const { data } = await api.get(`/afip/contribuyente/${cuit}`)
      const prov = matchProvincia(data.provincia || '')
      const caba = esCapitalFederal(prov)
      setForm(f => ({
        ...f,
        tipo_doc: '80',
        nombre: data.nombre || f.nombre,
        tipo_resp: data.tipo_resp != null ? String(parseFloat(data.tipo_resp)) : f.tipo_resp,
        domicilio: data.domicilio || f.domicilio,
        localidad: caba ? f.localidad : (data.localidad || f.localidad),
        provincia: prov || f.provincia,
        codigo_postal: data.codigo_postal || f.codigo_postal,
      }))
      setAfipStatus('ok')
      const cond = { RI: 'Resp. Inscripto', MONOTRIBUTO: 'Monotributo', EXENTO: 'Exento', CF: 'Cons. Final' }[data.condicion_iva]
      setAfipMsg(`Validado: ${data.nombre}${cond ? ` · ${cond}` : ''}`)
    } catch (err) {
      setAfipStatus('error')
      setAfipMsg(err.response?.data?.error || 'No se pudo validar en AFIP.')
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setErrores({})
    try {
      const payload = {
        ...form,
        tipo_doc: form.tipo_doc ? parseInt(form.tipo_doc) : null,
        tipo_resp: form.tipo_resp ? parseFloat(form.tipo_resp) : null,
        condicion: form.condicion ? parseInt(form.condicion) : null,
        localidad: esCaba ? null : (form.localidad || null),
        codigo_postal: form.codigo_postal || null,
      }
      const { data } = await proveedoresApi.crear(payload)
      onCreado(data)
    } catch (err) {
      const d = err.response?.data
      setErrores({ ...(d?.errors || {}), general: [d?.message || 'Error al guardar.'] })
    } finally { setSaving(false) }
  }

  return (
    <Modal title="Nuevo proveedor" onClose={onClose} maxWidth="640px">
      <form onSubmit={handleSave} style={st.form}>
        {errores.general && <div style={st.errorBox}>{errores.general[0]}</div>}
        <div style={st.aviso}>El proveedor queda <b>solo en LGR</b>, sin número de Presea.</div>

        <div style={st.row2}>
          <div style={{ flex: 1 }}>
            <label style={st.label}>CUIT / Documento</label>
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <input style={{ ...st.input, flex: 1 }} value={form.cuit} autoFocus
                onChange={e => { set('cuit')(e); setAfipStatus(null); setAfipMsg('') }}
                onBlur={() => { if (form.cuit.replace(/\D/g, '').length === 11 && afipStatus === null) validarAfip() }}
                placeholder="XX-XXXXXXXX-X" autoComplete="off" />
              {form.cuit.replace(/\D/g, '').length === 11 && (
                <Boton variante="secondary" sm type="button" onClick={validarAfip} disabled={afipStatus === 'loading'} title="Validar en AFIP">
                  {afipStatus === 'loading' ? '...' : 'AFIP'}
                </Boton>
              )}
            </div>
            {afipMsg && <span style={{ ...st.err, color: afipStatus === 'ok' ? '#166534' : '#dc2626' }}>{afipMsg}</span>}
            {afipStatus === 'ok' && <span style={st.afipBadge}>Validado AFIP</span>}
            {fieldErr('cuit') && <span style={st.err}>{fieldErr('cuit')}</span>}
          </div>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Tipo de documento</label>
            <select style={st.input} value={form.tipo_doc} onChange={set('tipo_doc')}>
              <option value="">— Seleccionar —</option>
              {TIPOS_DOC.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>

        <div style={st.row2}>
          <div style={{ flex: 2 }}>
            <label style={st.label}>Razón social *</label>
            <input style={st.input} value={form.nombre} onChange={set('nombre')} required />
            {fieldErr('nombre') && <span style={st.err}>{fieldErr('nombre')}</span>}
          </div>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Condición IVA</label>
            <select style={st.input} value={form.tipo_resp} onChange={set('tipo_resp')}>
              <option value="">— Seleccionar —</option>
              {TIPOS_RESP.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label style={st.label}>Nombre de fantasía</label>
          <input style={st.input} value={form.nombre_fantasia} onChange={set('nombre_fantasia')} />
        </div>

        <div>
          <label style={st.label}>Domicilio</label>
          <CampoDireccion
            value={form.domicilio}
            onChange={v => setForm(f => ({ ...f, domicilio: v }))}
            onPlaceSelected={d => setForm(f => ({
              ...f,
              domicilio: d.calle_completa || d.calle || f.domicilio,
              localidad: d.localidad || f.localidad,
              provincia: d.provincia || f.provincia,
              codigo_postal: d.codigo_postal || f.codigo_postal,
            }))}
            inputStyle={st.input}
            placeholder="Calle y número"
          />
        </div>

        <div style={st.row2}>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Provincia</label>
            <select style={st.input} value={provinciaCanon} onChange={set('provincia')}>
              <option value="">— Seleccionar —</option>
              {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          {!esCaba && (
            <div style={{ flex: 1 }}>
              <label style={st.label}>Localidad</label>
              <CampoLocalidad provincia={provinciaCanon} value={form.localidad} onChange={v => setForm(f => ({ ...f, localidad: v }))} inputStyle={st.input} cp={form.codigo_postal} />
            </div>
          )}
          <div style={{ flex: esCaba ? 1 : 0.5 }}>
            <label style={st.label}>Código postal</label>
            <input style={st.input} value={form.codigo_postal} onChange={set('codigo_postal')} />
          </div>
        </div>

        <div style={st.row2}>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Contacto (vendedor del proveedor)</label>
            <input style={st.input} value={form.vendedor} onChange={set('vendedor')} autoComplete="off" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Teléfono</label>
            <input style={st.input} value={form.telefono} onChange={set('telefono')} autoComplete="off" />
          </div>
        </div>

        <div>
          <label style={st.label}>Email</label>
          <input style={st.input} value={form.email} onChange={set('email')} placeholder="compras@proveedor.com (varios separados por coma)" />
          {fieldErr('email') && <span style={st.err}>{fieldErr('email')}</span>}
        </div>

        <div style={st.row2}>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Condición de pago</label>
            <select style={st.input} value={form.condicion} onChange={set('condicion')}>
              <option value="">— Seleccionar —</option>
              {condiciones.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={st.label}>Rubro</label>
            <input style={st.input} value={form.rubro} onChange={set('rubro')} placeholder="Ej.: BODEGAS" />
          </div>
        </div>

        <div style={st.actions}>
          <Boton variante="ghost" type="button" onClick={onClose}>Cancelar</Boton>
          <Boton variante="primary" type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Crear proveedor'}</Boton>
        </div>
      </form>
    </Modal>
  )
}

const st = {
  form: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  row2: { display: 'flex', gap: '0.75rem' },
  label: { display: 'block', fontWeight: '600', fontSize: '0.8125rem', color: '#374151', marginBottom: '0.2rem' },
  input: { width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', boxSizing: 'border-box' },
  err: { color: '#dc2626', fontSize: '0.75rem', display: 'block', marginTop: '2px' },
  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.875rem' },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '0.25rem' },
  aviso: { background: '#f8fafc', border: '1px solid #e2e8f0', color: '#475569', padding: '0.5rem 0.75rem', borderRadius: '6px', fontSize: '0.8125rem' },
  afipBadge: { display: 'inline-block', marginTop: '2px', background: '#dcfce7', color: '#166534', fontSize: '0.7rem', fontWeight: '700', padding: '1px 6px', borderRadius: '10px' },
}
